import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  LogOut,
  Lock,
  UserPlus,
  MapPin,
  Shield,
  Wallet,
  User,
  Mail,
  Phone,
  Calendar,
  Globe,
  CreditCard,
  ChevronRight,
  Edit,
} from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import { supabase } from "@/lib/supabase";

export default function Profile() {
  const navigate = useNavigate();
  const { user: authUser, signOut } = useAuth();
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccountData();
  }, [authUser?.email]);

  const fetchAccountData = async () => {
    if (!authUser?.email) return;

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", authUser.email)
        .single();

      if (data && !error) {
        setAccountData(data);
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setLoading(false);
    }
  };

  // User profile data
  const user = {
    name:
      accountData?.first_name && accountData?.last_name
        ? `${accountData.first_name} ${accountData.last_name}`
        : authUser?.user_metadata?.firstName &&
          authUser?.user_metadata?.lastName
        ? `${authUser.user_metadata.firstName} ${authUser.user_metadata.lastName}`
        : authUser?.email?.split("@")[0] || "User",
    email: authUser?.email || "",
    preferredName: accountData?.preferred_name || "",
    phone: accountData?.phone_number || "",
    nationality: accountData?.nationality || "",
    idType: accountData?.id_type || "",
    idNumber: accountData?.id_number || "",
    kycStatus: accountData?.kyc_status === "approved",
    walletStatus: accountData?.wallet_id ? true : false,
    oxpayStatus: accountData?.oxpay_user_id
      ? accountData?.oxpay_status || "active"
      : "not_created",
    addressCompleted: accountData?.oxpay_address_completed || false,
  };

  const handleLogout = async () => {
    await signOut();
    // Navigation is handled by AuthContext
  };

  const handleResetPassword = () => {
    navigate("/reset-password");
  };

  const BulletIcon = () => (
    <svg
      width="12"
      height="15"
      viewBox="0 0 12 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.55">
        <mask id="path-1-inside-1_307_4128" fill="white">
          <path d="M12 4.3667V11.3667L6 14.8667L0 11.3667V4.3667L6 0.866699L12 4.3667ZM6.00098 4.94971C4.40667 4.94971 3.11426 6.25587 3.11426 7.8667C3.11428 9.47751 4.40668 10.7837 6.00098 10.7837C7.59506 10.7834 8.88768 9.47736 8.8877 7.8667C8.8877 6.25602 7.59507 4.94996 6.00098 4.94971Z" />
        </mask>
        <path
          d="M12 4.3667H12.8V3.9072L12.4031 3.67568L12 4.3667ZM12 11.3667L12.4031 12.0577L12.8 11.8262V11.3667H12ZM6 14.8667L5.5969 15.5577L6 15.7929L6.4031 15.5577L6 14.8667ZM0 11.3667H-0.8V11.8262L-0.403097 12.0577L0 11.3667ZM0 4.3667L-0.403097 3.67568L-0.8 3.9072V4.3667H0ZM6 0.866699L6.4031 0.175676L6 -0.0594637L5.5969 0.175676L6 0.866699ZM6.00098 4.94971L6.0011 4.14971H6.00098V4.94971ZM3.11426 7.8667H2.31426V7.86671L3.11426 7.8667ZM6.00098 10.7837V11.5837H6.0011L6.00098 10.7837ZM8.8877 7.8667L9.6877 7.86671V7.8667H8.8877ZM12 4.3667H11.2V11.3667H12H12.8V4.3667H12ZM12 11.3667L11.5969 10.6757L5.5969 14.1757L6 14.8667L6.4031 15.5577L12.4031 12.0577L12 11.3667ZM6 14.8667L6.4031 14.1757L0.403097 10.6757L0 11.3667L-0.403097 12.0577L5.5969 15.5577L6 14.8667ZM0 11.3667H0.8V4.3667H0H-0.8V11.3667H0ZM0 4.3667L0.403097 5.05772L6.4031 1.55772L6 0.866699L5.5969 0.175676L-0.403097 3.67568L0 4.3667ZM6 0.866699L5.5969 1.55772L11.5969 5.05772L12 4.3667L12.4031 3.67568L6.4031 0.175676L6 0.866699ZM6.00098 4.94971V4.14971C3.95696 4.14971 2.31426 5.82196 2.31426 7.8667H3.11426H3.91426C3.91426 6.68977 4.85638 5.74971 6.00098 5.74971V4.94971ZM3.11426 7.8667L2.31426 7.86671C2.31428 9.91141 3.95696 11.5837 6.00098 11.5837V10.7837V9.98369C4.8564 9.98369 3.91427 9.04362 3.91426 7.86669L3.11426 7.8667ZM6.00098 10.7837L6.0011 11.5837C8.04468 11.5834 9.68767 9.91138 9.6877 7.86671L8.8877 7.8667L8.0877 7.86669C8.08768 9.04333 7.14543 9.98351 6.00085 9.98369L6.00098 10.7837ZM8.8877 7.8667H9.6877C9.6877 5.82199 8.04468 4.15003 6.0011 4.14971L6.00098 4.94971L6.00085 5.74971C7.14546 5.74989 8.0877 6.69006 8.0877 7.8667H8.8877Z"
          fill="#242424"
          mask="url(#path-1-inside-1_307_4128)"
        />
      </g>
    </svg>
  );

  return (
    <div>
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="p-3">
        <div className="w-full max-w-md mx-auto flex flex-col gap-9">
          {/* Profile Title */}
          <div className="px-1 flex justify-between items-center">
            <h1 className="text-sm font-medium text-foreground">Profile</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/edit-user")}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit Profile
            </Button>
          </div>

          {/* User Info Card */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex flex-col gap-7">
                {/* Full Name */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">Full Name</span>
                    <span className="text-xs text-[#646464]">{user.name}</span>
                  </div>
                </div>

                {/* Preferred Name */}
                {user.preferredName && (
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3.5 opacity-55">
                      <BulletIcon />
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-xs text-[#646464]">
                        Preferred Name
                      </span>
                      <span className="text-xs text-[#646464]">
                        {user.preferredName}
                      </span>
                    </div>
                  </div>
                )}

                {/* Email */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">Email</span>
                    <span className="text-xs text-[#646464]">{user.email}</span>
                  </div>
                </div>

                {/* Phone Number */}
                {user.phone && (
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3.5 opacity-55">
                      <BulletIcon />
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-xs text-[#646464]">
                        Phone Number
                      </span>
                      <span className="text-xs text-[#646464]">
                        {user.phone.startsWith("+")
                          ? user.phone
                          : `+${user.phone}`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Nationality */}
                {user.nationality && (
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3.5 opacity-55">
                      <BulletIcon />
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-xs text-[#646464]">
                        Nationality
                      </span>
                      <span className="text-xs text-[#646464]">
                        {user.nationality}
                      </span>
                    </div>
                  </div>
                )}

                {/* Passport Number */}
                {user.idNumber && (
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3.5 opacity-55">
                      <BulletIcon />
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-xs text-[#646464]">
                        Passport Number
                      </span>
                      <span className="text-xs text-[#646464]">
                        {user.idNumber}
                      </span>
                    </div>
                  </div>
                )}

                {/* User Registration Status */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">
                      User Registration Status
                    </span>
                    <span
                      className={`text-xs font-bold ${
                        user.oxpayStatus === "active"
                          ? "text-green-500"
                          : user.oxpayStatus === "suspended"
                          ? "text-yellow-500"
                          : "text-[#646464]"
                      }`}
                    >
                      {user.oxpayStatus === "active"
                        ? "Registered"
                        : user.oxpayStatus === "suspended"
                        ? "Suspended"
                        : "Not Registered"}
                    </span>
                  </div>
                </div>

                {/* Address Input Status */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">
                      Address Input Status
                    </span>
                    <span
                      className={`text-xs font-bold ${
                        user.addressCompleted === true
                          ? "text-green-500"
                          : user.addressCompleted === false
                          ? "text-yellow-500"
                          : "text-[#646464]"
                      }`}
                    >
                      {user.addressCompleted === true
                        ? "Completed"
                        : user.addressCompleted === false
                        ? "Pending"
                        : "Not Completed"}
                    </span>
                  </div>
                </div>

                {/* KYC Verification Status */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">
                      KYC Verification Status
                    </span>
                    <span
                      className={`text-xs font-bold ${
                        user.kycStatus ? "text-green-500" : "text-[#646464]"
                      }`}
                    >
                      {user.kycStatus ? "Approved" : "Not Approved"}
                    </span>
                  </div>
                </div>

                {/* Wallet Status */}
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3.5 opacity-55">
                    <BulletIcon />
                  </div>
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-xs text-[#646464]">
                      Wallet Status
                    </span>
                    <span
                      className={`text-xs font-bold ${
                        user.walletStatus ? "text-green-500" : "text-[#646464]"
                      }`}
                    >
                      {user.walletStatus ? "Created" : "Not Created"}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Reset Password Button */}
            <Button
              onClick={handleResetPassword}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center justify-between px-4"
            >
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-4" />
                <span className="text-xs font-medium tracking-wide">
                  Reset Password
                </span>
              </div>
              <span className="text-sm">〉</span>
            </Button>

            {/* Log Out Button */}
            <Button
              onClick={handleLogout}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center justify-between px-4"
            >
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                <span className="text-xs font-medium tracking-wide">
                  Log Out
                </span>
              </div>
              <span className="text-sm">〉</span>
            </Button>
          </div>
        </div>
      </div>

      <BottomBar />
    </div>
  );
}
